/**
 * Look for environment variables in the following places:
 *
 * window.variables - Used when serving the application from within a war
 * process.env - Used when serving the app from the development environment or serving the app statically.
 *               the variable name is prefixed with "REACT_APP_", uppercased and all dots converted to underscores.
 *               (This keeps it inline with naming conventions used by Create React App)
 *
 * If the variable is not found in either place the defaultValue is returned
 * @param variable
 * @param defaultValue
 * @returns {*}
 */

declare global {
  interface Window {
    variables: { [key: string]: string }
  }
}

export function envString(variable: string, defaultValue ?: string): string | undefined {

  let value = (window.variables && window.variables[variable]) || undefined
  if (typeof value !== 'undefined') {
    return value
  } else {
    value = import.meta.env[getReactName(variable)]
    if (typeof value !== 'undefined') {
      if (import.meta.env.DEV) {
        console.info (`✅ Found environment variable with name ${variable} and value "${value}"`)
      }
      return value
    } else {
      if (import.meta.env.DEV) {
        console.info(`❌ No environment variable with name "${variable}" found. Defaulting to: "${defaultValue}"`)
      }
      return defaultValue
    }
  }
}

export function envBoolean(variable: string, defaultValue ?: boolean): boolean | undefined {
  const value = envString(variable)
  if (typeof value !== 'undefined') {
    return value.toLowerCase() === 'true'
  } else {
    return defaultValue
  }
}

export function envNumber(variable: string, defaultValue ?: number): number | undefined {
  const value = envString(variable)
  if (typeof value !== 'undefined') {
    return Number(value)
  } else {
    return defaultValue
  }
}

/**
 * Convert a environment variable name to a react environment variable name.
 *
 * eg.
 *
 * building.host
 *
 * becomes:
 *
 * VITE_BUILDING_HOST
 */
function getReactName(variableName: string) {
  return 'VITE_' + variableName.toUpperCase().replace(/\./g, '_')
}